export const COST_BOM_DEMO_STRINGS = {
  composerSourcing: 'Sourcing',
  /* Names the model below the composer for what it is. Without it the thing
     builds itself on load and reads as the visitor's own result. */
  exampleLabel: 'Cost model example',
  /* The send button carries the promise, so it names what arrives rather
     than what the click does. A model built and emailed is the only thing we
     can offer until the platform handoff exists. */
  composerSend: 'Get My Cost Model for Free',
  /* Shown in the email card when it is submitted without a product. An ask,
     not an error: they have not done the step yet, which is different from
     having done it wrong. */
  composerNeedsProduct: 'Add a product first, a line is enough.',
  composerPlaceholder: 'Describe your product',
  /* The placeholder's fixed half. It states what the field does, so the part
     that moves is only ever the route in. */
  composerStem: 'Build a model ',
  /* Typed after the stem, one after another. The field takes free text, so
     what it accepts is not guessable from an empty box: each line names one
     way in. */
  composerExamples: [
    'from a description',
    'from a CAD, BOM, or spec file',
    'from a product URL',
    'from connected tools',
  ],
  sourcingFrom: ' sourcing from ',
  generate: 'Generate Should-Cost Model',
  buildYourOwn: 'Build Your Own Cost Model',
  addSample: 'View another sample',
  yourOwnProduct: 'Your own product +',
  describeProduct: 'Sign up to model it for free',
  estimatedShouldCost: 'Estimated should cost',
  costComposition: 'Cost composition',
  totalMass: 'Total mass',
  currency: 'USD',
  sourcedFrom: 'Sourced from',
  whereCostSits: 'Where the cost sits',
  /**
   * Claims the drivers pane can make about a model, each true only under its own
   * condition. `{name}` is the largest component, `{share}`/`{times}` its size
   * against the product or the runner-up, `{driver}` a non-material cost bucket.
   */
  dominanceHeadline: '{name} alone is {share} of product cost',
  processParityHeadline: '{driver} costs as much as the largest component',
  concentrationHeadline:
    '{n} of {total} components carry {share} of total cost',
  dominanceClosing: '{name} costs {times} the next component',
  materialClosing:
    '{materialShare} of the {materials} material cost sits in one component',
  searchCountries: 'Search countries',
  noCountriesFound: 'No countries found',
  /* The success state: what happens next, then the one offer still open. */
  sentTitle: 'On its way',
  sentBody: 'Muir is building it now and will send it to',
  sentExpertLede: 'Want help reading it when it lands?',
  reportTitle: 'Your cost model, free',
  reportLede: 'Muir will send it to your inbox shortly.',
  recapProduct: 'Product',
  emailLabel: 'Email',
  emailPlaceholder: 'you@company.com',
  emailInvalid: 'Enter an email we can send it to',
  reportSend: 'Email Me the Cost Model',
  notNow: 'Not now',
  dismiss: 'Dismiss',
  talkToExpert: 'Talk to an Expert',
  businessEmailError: 'Please use a business email address',
  emailRequired: 'Email is required',
} as const

/** Where the "build your own cost model" CTAs send visitors to start a trial. */
export const REGISTER_URL = 'https://app.muir.ai/register'

/** Sales route for the "talk to an expert" action. */
export const CALENDLY_URL = 'https://calendly.com/harris-muir/muir-demo-request'

export const SAMPLE_PRODUCTS = [
  {
    product: 'NMC Battery Cell',
    country: 'China',
    key: 'nmc',
  },
  {
    product: 'Citric Acid',
    country: 'Germany',
    key: 'citric',
  },
  {
    product: 'Smart Home SoC',
    country: 'Taiwan',
    key: 'soc',
  },
] as const

/**
 * Mass unit per sample. The SoC is modelled in kilograms like everything else
 * but weighs half a gram, so its column scales; the band reads this too, or a
 * die would show as 0.0005 kg above the 0.5 g in the row beneath it.
 */
export const SAMPLE_UNITS: Record<
  string,
  { massFactor: number; mass: string }
> = {
  default: { massFactor: 1, mass: 'kg' },
  nmc: { massFactor: 1, mass: 'kg' },
  citric: { massFactor: 1, mass: 'kg' },
  soc: { massFactor: 1000, mass: 'g' },
}

/**
 * The four cost drivers, in the order they are stacked on a row's cost bar.
 * The names double as the keys of a node's `breakdown`, so the legend and the
 * per-row breakdown card always read the same.
 */
export const COST_DRIVERS = [
  { key: 'Materials', modifier: 'materials' },
  { key: 'Labor', modifier: 'labor' },
  { key: 'Overhead', modifier: 'overhead' },
  { key: 'Value Add', modifier: 'valueadd' },
] as const
